import { Dialog, DialogTitle, DialogHeader, DialogContent } from "@/components/ui/dialog";
import { useGetOneExpeditionBo, useUpdateCustomQuotationQuoteBo } from "@/api/expedition.api";
import {
  Loader2,
  X,
  CircleCheck,
  CircleX,
  ArrowRightLeft,
  MessageCircleMore,
  FileText,
  NotebookPen,
} from "lucide-react";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetAllCompanyDto,
  GetOneExpeditionBoResponseDto,
  GetCustomsV1Response,
} from "@/types/api/api-types";
import { Button } from "@/components/ui/button";

import { useGetCompanyBOV1 } from "@/api/company.api";
import { cn } from "@/lib/utils";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TypeEnvoi } from "@/components/shared/badge/typeEnvoi.badge";
import {
  CustomCotationQuoteFormData,
  CustomCotationQuoteSchema,
} from "../customQuotationQuote.form";
import { CustomQuotationOtherTypeBadge } from "@/components/shared/badge/customQuotationOtherType.badge";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { InfoQuote } from "./infoQuote";
import { getCustomQuotationQuoteDefault } from "./customQuotationQuoteDefault";
import { CustomQuotationTypeBadge } from "@/components/shared/badge/customQuotationType.badge";
import { PalettePackageInfo, PaletteProductInfo } from "./paletteInfo";
import { ParcelPackageInfo, ParcelProductInfo } from "./parcelInfo";
import { toast } from "react-toastify";
import { ActionDataBo } from "@/types/tableAction";
import { Bell } from "lucide-react";
import { NotificationModal } from "@/components/shared/modal/notification.modal";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NotesModal } from "@/components/shared/modal/notes.modal";
import { Badge } from "@/components/ui/badge";
import { DocumentListItem } from "@/components/shared/documentListItem";
import { sortDocumentsByPriority } from "@/constants/fileCrud";
import { useGetCustoms } from "@/api/customs.api";

type Props = {
  expeditionRow: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
  open: boolean;
  onClose: () => void;
};

type ModalContentProps = {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  customs: GetCustomsV1Response;
  onClose: () => void;
};

type MainModalProps = {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  isPending: boolean;
  customs: GetCustomsV1Response;
};

type ProtectedInformationProps = {
  value: React.ReactNode;
  label: string;
  onClick?: () => void;
};

type SideModalProps = {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  onClose: () => void;
  setActionData: (actionData: ActionDataBo) => void;
};

type InformationNodeDisplayProps = {
  value: React.ReactNode;
  label: string;
};

export const CustomQuotationQuoteBoModal = ({ expeditionRow, open, onClose }: Props) => {
  const {
    data: expedition,
    isLoading: isLoadingExpedition,
    isError: isErrorExpedition,
  } = useGetOneExpeditionBo(expeditionRow.id);
  const {
    data: company,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
  } = useGetCompanyBOV1(expeditionRow.companyId);
  const {
    data: customs,
    isPending: isCustomsPending,
    isError: isErrorCustoms,
  } = useGetCustoms({
    isArchived: false,
  });

  const { t } = useTranslation("boQuotations");
  const isLoading = isLoadingExpedition || isLoadingCompany || isCustomsPending;
  const isError = isErrorExpedition || isErrorCompany || isErrorCustoms;

  if (isLoading) {
    return (
      <Dialog open={open}>
        <DialogContent
          className="max-h-[90vh] max-w-[90vw] overflow-y-auto xl:max-w-[1000px]"
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle>{t("quoteModal.title")}</DialogTitle>
          </DialogHeader>
          <Loader2 className="animate-spin" />
        </DialogContent>
      </Dialog>
    );
  }

  if (isError || !expedition || !company || !customs) {
    return (
      <Dialog open={open}>
        <DialogContent className="max-w-[400px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t("quoteModal.title")}</DialogTitle>
          </DialogHeader>
          <p>{t("quoteModal.error")}</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-h-[90vh] max-w-[90vw] overflow-y-hidden border-none bg-transparent shadow-none xl:max-w-[1600px]"
        aria-describedby={undefined}
        showClose={false}
      >
        <DialogTitle aria-describedby="expedition-finaliser" />
        <ModalContent
          expedition={expedition}
          company={company}
          customs={customs}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};

const ProtectedInformation = ({ label, value, onClick }: ProtectedInformationProps) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <p className="text-base font-medium text-gray-900">{label} :</p>
      {value}
      {onClick && (
        <Button variant="ghost" onClick={onClick} type="button">
          <ArrowRightLeft />
        </Button>
      )}
    </div>
  );
};

const InformationDisplay = ({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) => {
  return (
    <div className="items-top flex flex-row gap-2">
      <p className={cn("w-fit min-w-fit text-sm text-gray-900", bold && "font-bold")}>{label} :</p>
      <p className={cn("text-sm text-gray-900", bold && "font-bold")}>{value}</p>
    </div>
  );
};

export const InformationNodeDisplay = ({ label, value }: InformationNodeDisplayProps) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <p className="text-base font-medium text-gray-900">{label} :</p>
      {value}
    </div>
  );
};

const ModalContent = ({ expedition, company, customs, onClose }: ModalContentProps) => {
  const { mutateAsync: updateCustomQuotationQuoteBo, isPending } =
    useUpdateCustomQuotationQuoteBo();
  const { t } = useTranslation("boQuotations");
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);

  const method = useForm<CustomCotationQuoteFormData>({
    resolver: zodResolver(CustomCotationQuoteSchema),
    defaultValues: getCustomQuotationQuoteDefault(expedition),
  });

  const checkForUnreadNotifications = () => {
    if (
      expedition.notification &&
      expedition.notification.length > 0 &&
      expedition.notification.some((notification) => notification.readBy === null)
    ) {
      toast.error(t("quoteModal.unreadNotifications"), {
        position: "top-center",
        autoClose: 5000,
      });
      return true;
    }
    return false;
  };

  const onSubmit = async (data: CustomCotationQuoteFormData) => {
    if (checkForUnreadNotifications()) {
      return;
    }
    try {
      await updateCustomQuotationQuoteBo({
        id: expedition.id,
        payload: data,
      });
      onClose();
    } catch (error) {
      toast.error(t("quoteModal.updateError"));
    }
  };

  return (
    <FormProvider {...method}>
      <form
        onSubmit={method.handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
      >
        <div className="flex h-[80vh] w-full flex-col-reverse justify-between gap-2 overflow-y-hidden lg:flex-row">
          <MainModal
            expedition={expedition}
            company={company}
            isPending={isPending}
            customs={customs}
          />
          <CustomQuotationSideModal
            expedition={expedition}
            company={company}
            onClose={onClose}
            setActionData={setActionData}
          />
        </div>
      </form>
      {actionData && actionData.action === "NOTIFICATION" && (
        <NotificationModal
          expeditionId={actionData.expedition.id}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
      {actionData && actionData.action === "NOTES" && (
        <NotesModal
          expedition={actionData.expedition}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
    </FormProvider>
  );
};

export const CustomQuotationSideModal = ({
  expedition,
  company,
  onClose,
  setActionData,
}: SideModalProps) => {
  const { t } = useTranslation("boQuotations");
  const expeditionIdentifier = expedition
    ? expedition?.quotation
      ? `C-${expedition.quotation.identifier}`
      : `D-${expedition.customQuotation!.identifier}`
    : t("quoteSidePanel.identifierError");

  const hasUnreadNotif =
    (expedition.notification?.length ?? 0) > 0 &&
    expedition.notification?.some((n) => n.readBy == null);

  const pickupLocationId = expedition.pickupInfo?.pickupLocation?.id || "";
  const billingEntityId = expedition.expeditionBillingDetail?.billingEntityId;
  const express = expedition.expeditionOptions?.express;
  const insurance = expedition.expeditionOptions?.insurance;

  const isMultipleExpedition =
    expedition.customQuotation?.otherDetail?.isMultipleExpedition || false;

  const pickupLocation = company.pickUpLocations.find(
    (pickUpLocation) => pickUpLocation.id === pickupLocationId,
  );

  const billingEntity = company.billingEntities.find(
    (billingEntity) => billingEntity.id === billingEntityId,
  );

  const sortedDocuments = useMemo(
    () => sortDocumentsByPriority(expedition.expeditionDocuments?.documents ?? []),
    [expedition.expeditionDocuments?.documents],
  );

  const pickupAddress = pickupLocation
    ? pickupLocation?.street + ", " + pickupLocation?.postalCode + " " + pickupLocation?.city
    : expedition.pickupInfo?.pickupStreet +
      ", " +
      expedition.pickupInfo?.pickupPostalCode +
      " " +
      expedition.pickupInfo?.pickupCity +
      ", " +
      expedition.pickupInfo?.pickupCountry;

  return (
    <div className="relative flex h-full flex-1/6 flex-col gap-0 overflow-y-auto rounded-lg bg-white p-0">
      <div className="sticky top-0 z-50 flex flex-col gap-0 border-b bg-white p-4">
        <div className="flex flex-row items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-black">{expeditionIdentifier}</h3>
          <div className="flex flex-row items-center justify-between gap-0">
            <Button
              variant="ghost"
              onClick={() => setActionData({ expedition, action: "NOTES" })}
              className="relative h-12 w-12 [&_svg]:h-8 [&_svg]:w-8"
              type="button"
            >
              <NotebookPen className="h-4 w-4" />
              <Badge variant="destructive" className="absolute -top-0 -right-0 h-4 w-4">
                {expedition.expeditionNotes?.length}
              </Badge>
            </Button>
            <Button
              variant="ghost"
              onClick={() => setActionData({ expedition, action: "NOTIFICATION" })}
              className={cn(
                "relative h-12 w-12 [&_svg]:h-8 [&_svg]:w-8",
                hasUnreadNotif && "text-yellow-500",
              )}
              type="button"
            >
              <Bell className="h-4 w-4" />
              <Badge variant="destructive" className="absolute -top-0 -right-0 h-4 w-4">
                {expedition.notification?.length}
              </Badge>
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              type="button"
              className="h-12 w-12 text-red-600 [&_svg]:h-8 [&_svg]:w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="flex h-full flex-1/6 flex-col gap-2 overflow-y-auto rounded-lg bg-white px-4 pt-2">
        <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <h4 className="min-w-fit text-base font-bold text-gray-900">
            {t("quoteSidePanel.optionsTitle")}
          </h4>
          <InformationNodeDisplay
            label={t("quoteSidePanel.shippingTypeLabel")}
            value={<TypeEnvoi express={express} />}
          />
          <InformationNodeDisplay
            label={t("quoteSidePanel.insuranceLabel")}
            value={
              insurance ? (
                <CircleCheck className="h-6 w-6 fill-green-500 text-white" />
              ) : (
                <CircleX className="h-6 w-6 fill-red-500 text-white" />
              )
            }
          />
        </div>
        {expedition.customQuotation?.otherDetail && (
          <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="min-w-fit text-base font-bold text-gray-900">
                {t("quoteSidePanel.additionalInfoTitle")}
              </h4>
              <CustomQuotationOtherTypeBadge
                type={
                  expedition.customQuotation.otherDetail
                    ?.otherCustomQuotationType as OtherCustomQuotationTypeEnum
                }
              />
            </div>
            <ProtectedInformation
              label={t("quoteSidePanel.numberOfExpeditionsLabel")}
              value={(expedition.customQuotation.otherDetail?.numberOfExpedition || 1).toString()}
            />
            {!isMultipleExpedition ? (
              <>
                <InformationDisplay
                  label={t("quoteSidePanel.recipientLabel")}
                  value={
                    expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName
                  }
                />
                <InformationDisplay
                  label={t("quoteSidePanel.recipientCompanyLabel")}
                  value={expedition.deliveryInfo?.company || "-"}
                />
                <InformationDisplay
                  label={t("quoteSidePanel.addressLabel")}
                  value={
                    expedition.deliveryInfo?.street +
                    ", " +
                    expedition.deliveryInfo?.postalCode +
                    " " +
                    expedition.deliveryInfo?.city +
                    ", " +
                    expedition.deliveryInfo?.country
                  }
                />
                <InformationDisplay
                  label={t("quoteSidePanel.phoneLabel")}
                  value={expedition.deliveryInfo?.phone || "-"}
                />
                <InformationDisplay
                  label={t("quoteSidePanel.emailLabel")}
                  value={expedition.deliveryInfo?.email || "-"}
                />
              </>
            ) : (
              <InformationDisplay
                label={t("quoteSidePanel.recipientLabel")}
                value={t("quoteSidePanel.recipientUnavailableMultiple")}
              />
            )}
          </div>
        )}
        {expedition.customQuotation?.paletteDetail && (
          <>
            <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-fit text-base font-bold text-gray-900">
                  {t("quoteSidePanel.deliveryInfoTitle")}
                </h4>
              </div>
              <div className="flex flex-col gap-0">
                <InformationDisplay
                  label={t("quoteSidePanel.recipientLabel")}
                  value={
                    expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName
                  }
                />
                <InformationDisplay
                  label={t("quoteSidePanel.recipientCompanyLabel")}
                  value={expedition.deliveryInfo?.company || "-"}
                />
                <InformationDisplay
                  label={t("quoteSidePanel.addressLabel")}
                  value={
                    expedition.deliveryInfo?.street +
                    ", " +
                    expedition.deliveryInfo?.postalCode +
                    " " +
                    expedition.deliveryInfo?.city +
                    ", " +
                    expedition.deliveryInfo?.country
                  }
                />
                <InformationDisplay
                  label={t("quoteSidePanel.phoneLabel")}
                  value={expedition.deliveryInfo?.phone || "-"}
                />
                <InformationDisplay
                  label={t("quoteSidePanel.emailLabel")}
                  value={expedition.deliveryInfo?.email || "-"}
                />
              </div>
            </div>
            <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-fit text-base font-bold text-gray-900">
                  {t("quoteSidePanel.productInfoTitle")}
                </h4>
                <CustomQuotationTypeBadge
                  type={expedition.customQuotation.type as CustomQuotationTypeEnum}
                />
              </div>
              <p className="text-sm text-gray-500">{t("quoteSidePanel.productsSubtitle")}</p>
              {expedition.customQuotation.paletteDetail.paletteProducts.map(
                (paletteProduct, index) => (
                  <PaletteProductInfo
                    key={`palette-product-${index}`}
                    paletteProduct={paletteProduct}
                    index={index}
                  />
                ),
              )}
              <p className="text-sm text-gray-500">{t("quoteSidePanel.dimensionsSubtitle")}</p>
              {expedition.customQuotation.paletteDetail.palettePackages.map(
                (palettePackage, index) => (
                  <PalettePackageInfo
                    key={`palette-package-${index}`}
                    palettePackage={palettePackage}
                    index={index}
                  />
                ),
              )}
            </div>
          </>
        )}
        {expedition.customQuotation?.parcelDetail && (
          <>
            <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-fit text-base font-bold text-gray-900">
                  {t("quoteSidePanel.deliveryInfoTitle")}
                </h4>
              </div>
              <div className="flex flex-col gap-0">
                <InformationDisplay
                  label={t("quoteSidePanel.recipientLabel")}
                  value={
                    expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName
                  }
                />
                <InformationDisplay
                  label={t("quoteSidePanel.recipientCompanyLabel")}
                  value={expedition.deliveryInfo?.company || "-"}
                />
                <InformationDisplay
                  label={t("quoteSidePanel.addressLabel")}
                  value={
                    expedition.deliveryInfo?.street +
                    ", " +
                    expedition.deliveryInfo?.postalCode +
                    " " +
                    expedition.deliveryInfo?.city +
                    ", " +
                    expedition.deliveryInfo?.country
                  }
                />
                <InformationDisplay
                  label={t("quoteSidePanel.phoneLabel")}
                  value={expedition.deliveryInfo?.phone || "-"}
                />
                <InformationDisplay
                  label={t("quoteSidePanel.emailLabel")}
                  value={expedition.deliveryInfo?.email || "-"}
                />
              </div>
            </div>
            <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-fit text-base font-bold text-gray-900">
                  {t("quoteSidePanel.productInfoTitle")}
                </h4>
                <CustomQuotationTypeBadge
                  type={expedition.customQuotation.type as CustomQuotationTypeEnum}
                />
              </div>
              <p className="text-sm text-gray-500">{t("quoteSidePanel.productsSubtitle")}</p>
              {expedition.customQuotation.parcelDetail.parcelProducts.map(
                (parcelProduct, index) => (
                  <ParcelProductInfo parcelProduct={parcelProduct} index={index} />
                ),
              )}
              <p className="text-sm text-gray-500">{t("quoteSidePanel.dimensionsSubtitle")}</p>
              {expedition.customQuotation.parcelDetail.parcelPackages.map(
                (parcelPackage, index) => (
                  <ParcelPackageInfo parcelPackage={parcelPackage} index={index} />
                ),
              )}
            </div>
          </>
        )}
        <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <div className="flex flex-row items-center gap-2">
            <h4 className="text-base font-bold text-gray-900">
              {t("quoteSidePanel.clientCommentTitle")}
            </h4>
            <MessageCircleMore className="fill-black text-gray-100" />
          </div>
          <p className="text-base text-gray-600">{expedition.deliveryInfo?.comment || "-"}</p>
        </div>
        <div id="fileContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <div className="flex flex-row items-center gap-2">
            <h4 className="text-base font-bold text-gray-900">
              {t("quoteSidePanel.documentsTitle")}
            </h4>
            <FileText className="fill-black text-gray-100" />
          </div>
          {sortedDocuments.length > 0 ? (
            <div className="flex flex-col">
              {sortedDocuments.map((doc) => (
                <DocumentListItem key={doc.id} file={doc} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t("quoteSidePanel.noDocuments")}</p>
          )}
        </div>
        <div id="userContainer" className="flex flex-col gap-0 p-2">
          <InformationDisplay
            label={t("quoteSidePanel.userLabel")}
            value={expedition.createdBy?.firstName + " " + expedition.createdBy?.lastName}
            bold
          />
          <InformationDisplay
            label={t("quoteSidePanel.phoneLabel")}
            value={expedition.createdBy?.mobilePhoneNumber || "-"}
          />
          <InformationDisplay
            label={t("quoteSidePanel.emailLabel")}
            value={expedition.createdBy?.email || "-"}
          />
        </div>
        <div id="billingContainer" className="flex flex-col gap-0 p-2">
          <InformationDisplay
            label={t("quoteSidePanel.billingLabel")}
            value={billingEntity?.companyName || "-"}
            bold
          />
          <InformationDisplay
            label={t("quoteSidePanel.emailLabel")}
            value={billingEntity?.email || "-"}
          />
        </div>
        {pickupLocation ? (
          <div id="pickupContainer" className="flex flex-col gap-0 p-2">
            <InformationDisplay
              label={t("quoteSidePanel.pickupLabel")}
              value={pickupLocation?.name || "-"}
              bold
            />
            <InformationDisplay
              label={t("quoteSidePanel.addressLabel")}
              value={pickupAddress || "-"}
            />
            <InformationDisplay
              label={t("quoteSidePanel.phoneLabel")}
              value={pickupLocation?.contactNumber || "-"}
            />
          </div>
        ) : (
          <div id="pickupContainer" className="flex flex-col gap-0 p-2">
            <InformationDisplay
              label={t("quoteSidePanel.pickupLabel")}
              value={
                expedition.pickupInfo?.pickupCompany ||
                expedition.pickupInfo?.pickupFirstName +
                  " " +
                  expedition.pickupInfo?.pickupLastName ||
                "-"
              }
              bold
            />
            <InformationDisplay
              label={t("quoteSidePanel.addressLabel")}
              value={pickupAddress || "-"}
            />
            <InformationDisplay
              label={t("quoteSidePanel.phoneLabel")}
              value={expedition.pickupInfo?.pickupPhone || "-"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const MainModal = ({ isPending, expedition, company, customs }: MainModalProps) => {
  const { t } = useTranslation("boQuotations");
  return (
    <div className="flex flex-1/2 flex-col gap-1 overflow-y-auto">
      <InfoQuote
        isMultipleExpedition={
          expedition.customQuotation?.otherDetail?.isMultipleExpedition || false
        }
        processStatus={expedition.processStatus}
        expedition={expedition}
        company={company}
        customs={customs}
      />
      <Button
        variant={"outline"}
        type="submit"
        className="mt-2 w-[200px] self-end"
        disabled={isPending}
      >
        {isPending ? <Loader2 className="animate-spin" /> : t("quoteModal.submit")}
      </Button>
    </div>
  );
};
